import { useCallback, useEffect, useRef, useState } from 'react';

// Utility to get a nested value using a path array.
export function getNested<T>(
  obj: any,
  path: (string | number)[]
): T | undefined {
  return path.reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}

// Utility to set a nested value using a path array.
export function setNested(
  obj: any,
  path: (string | number)[],
  value: any
): void {
  let current = obj;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]!;
    if (current[key] === undefined) {
      current[key] = typeof path[i + 1] === 'number' ? [] : {};
    }
    current = current[key];
  }
  current[path[path.length - 1]!] = value;
}

export type Subscriber = (changedPath: (string | number)[], value: any) => void;

export type FormStateReturn<T> = {
  getField: (path: Path) => T | undefined;
  updateField: (path: Path, value: unknown) => void;
  subscribe: (cb: Subscriber) => () => void;
  stateRef: React.MutableRefObject<T>;
  compareFn: (
    path: Path,
    changedPath: Path,
    state: Pick<FormStateReturn<T>, 'getField'>
  ) => boolean;
};

export type Path = (string | number)[];

export function useFormState<T>(
  initialState: T,
  compareFn?: (
    path: Path,
    changedPath: Path,
    state: Pick<FormStateReturn<T>, 'getField'>
  ) => boolean
): FormStateReturn<T> {
  const stateRef = useRef<T>(initialState);
  const subscribersRef = useRef<Set<Subscriber>>(new Set());

  const getField = (path: Path): T | undefined => {
    return getNested(stateRef.current, path);
  };

  function updateField(path: Path, value: unknown): void {
    setNested(stateRef.current, path, value);
    subscribersRef.current.forEach((cb) => cb(path, value));

    return getNested(stateRef.current, path);
  }

  function subscribe(callback: Subscriber): () => void {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }

  const defaultCompareFn = (path: Path, changedPath: Path) => {
    return path.join('.') === changedPath.join('.');
  };

  return {
    getField,
    updateField,
    subscribe,
    stateRef,
    compareFn: compareFn ?? defaultCompareFn,
  };
}

export function makeUseFormField<T>(
  createPath: (
    path: Path,
    formState: Pick<FormStateReturn<T>, 'getField'>
  ) => Path
) {
  return function useFormField(
    formState: FormStateReturn<T>,
    path: Path
  ): [T | undefined, (newValueFn: (existing: T) => T) => void] {
    const [value, setValue] = useState(() =>
      formState.getField(createPath(path, formState))
    );

    useEffect(() => {
      const callback: Subscriber = (changedPath, newValue) => {
        if (
          JSON.stringify(changedPath) ===
          JSON.stringify(createPath(path, formState))
        ) {
          setValue(newValue);
        }
      };
      const unsubscribe = formState.subscribe(callback);
      return unsubscribe;
    }, [formState, path]);

    const onChange = useCallback(
      (newValueFn: (existingValue: T) => T) => {
        formState.updateField(
          createPath(path, formState),
          newValueFn(formState.getField(createPath(path, formState))!)
        );
        setValue(formState.getField(createPath(path, formState)));
      },
      [formState, path]
    );

    return [value, onChange];
  };
}
